import { escapeHTML } from '../../utils/escape.js';
import { formatMonto } from '../../utils/formatters.js';
import { textoAvisoPagoPrevio } from '../../shared/liquidacion-pagos-policy.js';
import { loteLiquidable } from '../../shared/liquidacion-tablero-policy.js';
import { notaTurnoSinSalida } from '../../shared/fichadas-ui-policy.js';
import { mensajeFijoYaLiquidadoMes } from '../../shared/fichadas-ui-policy.js';

/**
 * `model.error.kind` (S2b follow-up a) is ONE of two vocabularies: the four
 * LOCAL roster-load-failure literals in `js/shared/liquidacion-tablero-policy.js`'s
 * `TABLERO_ERROR_KINDS` (`'empleados'|'fichadas'|'historial'|'fuera_de_ventana'`),
 * OR `admin-liquidacion.service.js`'s own `preview` envelope `kind` — copied
 * here instead of imported because it is a TYPE, not a value this file
 * calls at runtime.
 * @typedef {'empleados'|'fichadas'|'historial'|'fuera_de_ventana'|'auth'|'validacion'|'anomalias'|'fudo_bloqueado'|'tope_no_configurado'|'precondicion_bloqueante'|'rechazo_parcial'|'incierto'|'error'} TableroErrorKind
 */

/**
 * Vista del tablero de período de Liquidación (Unit S2a, design.md AD1/AD5,
 * spec `payroll-period-dashboard`). Función de render pura: `js/admin.js`
 * (wiring) le pasa el modelo ya resuelto — ningún cómputo de negocio vive
 * acá, solo formateo y armado de HTML (mismo criterio que
 * `LiquidacionView.js`/`ComprobanteLiquidacionView.js`).
 *
 * Reemplaza al selector mes/empleados como la forma principal de liquidar
 * (AD1): esta vista se monta en `#admin-liquidacion-tablero-mount`, un
 * hermano NUEVO de `#admin-liquidacion-mount` (que sigue existiendo debajo,
 * con el historial/configuración — `LiquidacionView.js` no se toca en esta
 * unidad).
 *
 * No hay selección de empleados acá (spec "Roster Split Into Three
 * Buckets", ampliada por S2b con un cuarto balde no previsto en el nombre
 * original de la spec): el roster completo se clasifica solo, client-side,
 * en CUATRO baldes ("Listo"/"N anomalías"/"Liquidada"/"Sin horas") — el
 * modelo ya llega clasificado, esta vista solo pinta cada balde con su pill
 * y sus columnas.
 *
 * `data-action="tablero-liquidar"` (S2a, cableado en S7): el botón existe y
 * está habilitado para una fila "Listo" (spec "Toggle reveals a leaving
 * employee's pending settlement" — "'Liquidar' is available on it").
 *
 * S7 (design.md AD15/AD16, spec "Row and Bulk Actions"/"Bulk Print Job"):
 * agrega la barra de acciones en lote ("Liquidar N listos" / "Imprimir
 * comprobantes (N)") — ambos botones son PUROS disparadores, el re-preview +
 * confirm (AD6) y el armado de los documentos viven enteros en `js/admin.js`.
 * S8: la barra suma "Exportar CSV" (spec "Row and Bulk Actions"), REUBICADO
 * acá desde el toolbar de exportación legacy de `LiquidacionView.js` —
 * mismo criterio de trigger puro, `js/admin.js` sigue dueño de leer el form
 * y llamar a `liquidacion-export-policy.js` (sin cambios).
 *
 * S5b (design.md AD14/AD18, spec `payroll-payments`): las filas "Liquidada"
 * traen `fila.pill` (`estadoPago(...).pill`, ya resuelto — esta vista sólo
 * formatea) al lado de la pill de estado, y CUALQUIER fila puede traer
 * `fila.avisoPagoPrevio` (`pagosPreviosDeAnuladas(...)`) — el aviso de "ya se
 * pagó bajo una liquidación anulada de este período" se renderiza debajo de
 * la pill sin importar el balde. "Registrar pago" se ofrece sólo en
 * "Liquidada" — es la única fila con un `liquidacion_id`/neto congelado.
 */

const PRESET_BTN =
    'px-3 py-2 rounded-xl text-[11px] font-black uppercase tracking-widest border border-border-subtle text-text-muted hover:text-text-primary focus:outline-none focus:border-brand data-[active=true]:bg-brand data-[active=true]:text-white data-[active=true]:border-brand';
const NAV_BTN =
    'px-3 py-2 rounded-xl text-xs font-black text-text-muted hover:text-text-primary disabled:opacity-40 disabled:pointer-events-none focus:outline-none focus:border-brand border border-border-subtle';
const ACTUALIZAR_BTN =
    'px-4 py-2 rounded-xl bg-brand text-white text-[11px] font-black uppercase tracking-widest shadow-lg disabled:opacity-60 disabled:pointer-events-none';
/** S12: a disabled row "Liquidar" (e.g. "Sin horas") must LOOK disabled, not only be `disabled` — same `disabled:opacity-60` as the bulk bar, plus a not-allowed cursor. */
const LIQUIDAR_BTN =
    'px-3 py-1.5 rounded-xl bg-brand text-white text-[10px] font-black uppercase tracking-widest shadow disabled:opacity-60 disabled:cursor-not-allowed disabled:shadow-none';
const ADELANTO_BTN =
    'px-3 py-1.5 ml-2 rounded-xl border border-border-subtle text-text-muted text-[10px] font-black uppercase tracking-widest hover:text-text-primary';
const CONFIG_BTN =
    'flex items-center gap-2 px-3 py-2 rounded-xl text-[11px] font-black uppercase tracking-widest border border-border-subtle text-text-muted hover:text-text-primary focus:outline-none focus:border-brand';
const BULK_LIQUIDAR_BTN =
    'px-4 py-2 rounded-xl bg-brand text-white text-[11px] font-black uppercase tracking-widest shadow-lg disabled:opacity-60 disabled:pointer-events-none';
const BULK_IMPRIMIR_BTN =
    'px-4 py-2 rounded-xl border border-border-subtle text-text-muted text-[11px] font-black uppercase tracking-widest hover:text-text-primary disabled:opacity-40 disabled:pointer-events-none';
/** S8 — mismo tratamiento visual que `BULK_IMPRIMIR_BTN` ("Exportar CSV" es la tercera acción de la barra en lote, spec "Row and Bulk Actions"). */
const BULK_EXPORTAR_BTN = BULK_IMPRIMIR_BTN;
/** S8 — mismo campo chico que `renderPersonalizadoForm` ya usa para `desde`/`hasta`. */
const EXPORT_FIELD =
    'bg-base border border-border-subtle rounded-xl px-3 py-2 text-xs text-text-primary focus:outline-none focus:border-brand';

const PILL = {
    listo: 'inline-block px-3 py-1 rounded-full bg-green-100 text-green-800 text-xs font-bold whitespace-nowrap',
    anomalias: 'inline-block px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-bold whitespace-nowrap',
    liquidada: 'inline-block px-3 py-1 rounded-full bg-gray-200 text-gray-700 text-xs font-bold whitespace-nowrap',
    // S9 (usability fix): "Sin horas" antes compartía el mismo gris que
    // "Liquidada" (spec "Row Status Presentation" original) — indistinguibles
    // sin leer el texto. Ahora usa un tono azulado/slate con borde propio,
    // "Liquidada" se mantiene gris sin cambios.
    sin_horas: 'inline-block px-3 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-300 text-xs font-bold whitespace-nowrap',
};

// S5b — spec `payroll-payments` "Derived Payment State" (literal tono table:
// pendiente gris, parcial amarillo, pagado verde). `fila.pill.tono` (from
// `estadoPago`, `liquidacion-pagos-policy.js`) indexes directly into this map.
const PAGO_PILL = {
    pendiente: 'inline-block ml-2 px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 text-[10px] font-bold whitespace-nowrap',
    parcial: 'inline-block ml-2 px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 text-[10px] font-bold whitespace-nowrap',
    pagado: 'inline-block ml-2 px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-[10px] font-bold whitespace-nowrap',
};

const PRESETS = [
    { kind: 'mes', label: 'Mes' },
    { kind: 'quincena-1', label: '1ª quincena' },
    { kind: 'quincena-2', label: '2ª quincena' },
    { kind: 'semana', label: 'Semana' },
    { kind: 'personalizado', label: 'Otro' },
];

/**
 * `periodo_desde`/`periodo_hasta`/`desde`/`hasta` son columnas o rangos
 * `YYYY-MM-DD` sin componente horario — manipulación de string pura, NUNCA
 * `Date`/`Intl` (mismo motivo que `liquidacion-comprobante-policy.js#formatFechaPeriodo`,
 * que además agrega el año; acá el pill "Liquidada · dd/mm a dd/mm" del
 * spec es deliberadamente sin año).
 * @param {string|null|undefined} fechaSql
 * @returns {string}
 */
function formatDiaMes(fechaSql) {
    if (typeof fechaSql !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(fechaSql)) return '—';
    const [, mes, dia] = fechaSql.split('-');
    return `${dia}/${mes}`;
}

/** Misma regla que {@link formatDiaMes}, con año — para el rótulo del período elegido en el toolbar. */
function formatFechaLarga(fechaSql) {
    if (typeof fechaSql !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(fechaSql)) return '—';
    const [anio, mes, dia] = fechaSql.split('-');
    return `${dia}/${mes}/${anio}`;
}

/**
 * `calculadoAt` es un instante REAL (ISO, `new Date().toISOString()` al
 * cerrar el recompute) — a diferencia de `formatDiaMes`/`formatFechaLarga`
 * arriba, esto SÍ pasa por `Date`/`Intl`, zona horaria explícita
 * `America/Argentina/Buenos_Aires` (mismo criterio que
 * `formatters.js#formatFechaHora`, pero sin el día — el toolbar ya muestra
 * el período aparte).
 * @param {string|null|undefined} iso
 * @returns {string}
 */
function formatHoraSolo(iso) {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('es-AR', {
        timeZone: 'America/Argentina/Buenos_Aires',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(date);
}

/**
 * ¿Qué preset está activo, para resaltar el botón correspondiente? Deriva
 * SOLO de `seleccion.kind`/`seleccion.mitad` — nunca de `desde`/`hasta`
 * resueltos, así que un `kind` desconocido/`null` simplemente no resalta
 * ninguno.
 * @param {object|null} seleccion
 * @returns {string|null}
 */
function presetActivo(seleccion) {
    if (!seleccion) return null;
    if (seleccion.kind === 'quincena') return seleccion.mitad === '2' ? 'quincena-2' : 'quincena-1';
    return seleccion.kind;
}

/** @param {{ desde?: string, hasta?: string }|null} draft @param {object|null} seleccion @returns {string} */
function renderPersonalizadoForm(draft, seleccion, activo) {
    const desde = draft?.desde ?? seleccion?.desde ?? '';
    const hasta = draft?.hasta ?? seleccion?.hasta ?? '';
    return `
        <form data-action-form="tablero-personalizado" class="flex items-center gap-2 mt-3 ${activo ? '' : 'hidden'}">
            <label class="sr-only" for="tablero-personalizado-desde">Desde</label>
            <input id="tablero-personalizado-desde" type="date" name="desde" value="${escapeHTML(desde)}" class="bg-base border border-border-subtle rounded-xl px-3 py-2 text-xs text-text-primary focus:outline-none focus:border-brand" />
            <span class="text-text-muted text-xs">a</span>
            <label class="sr-only" for="tablero-personalizado-hasta">Hasta</label>
            <input id="tablero-personalizado-hasta" type="date" name="hasta" value="${escapeHTML(hasta)}" class="bg-base border border-border-subtle rounded-xl px-3 py-2 text-xs text-text-primary focus:outline-none focus:border-brand" />
            <button type="submit" class="${ACTUALIZAR_BTN}">Aplicar</button>
        </form>
    `;
}

/**
 * S5b (design.md AD18): "ya se pagaron $X de una liquidación anulada de
 * este período (dd/mm a dd/mm)" — copia literal, `periodos` puede traer más
 * de un rango si dos liquidaciones anuladas distintas del período tienen
 * pagos activos (`pagosPreviosDeAnuladas`, `liquidacion-pagos-policy.js`).
 * @param {{ total: number, periodos: Array<{desde:string,hasta:string}> }} aviso
 * @returns {string}
 */
function formatAvisoPagoPrevio(aviso) {
    const periodosFormateados = (aviso.periodos ?? []).map((p) => `${formatDiaMes(p.desde)} a ${formatDiaMes(p.hasta)}`);
    return textoAvisoPagoPrevio({ total: aviso.total, periodosFormateados });
}

/** @param {{ total: number, periodos: Array<{desde:string,hasta:string}> }|null|undefined} aviso @returns {string} */
function renderAvisoPagoPrevio(aviso) {
    if (!aviso) return '';
    return `<p class="text-[10px] text-amber-700 mt-1" data-tablero-aviso-pago>${escapeHTML(formatAvisoPagoPrevio(aviso))}</p>`;
}

/** @param {{texto:string, tono:'pendiente'|'parcial'|'pagado'}|null|undefined} pill S5b — `estadoPago(...).pill`, ya resuelto por `js/admin.js`. @returns {string} */
function renderPagoPill(pill) {
    if (!pill) return '';
    const clase = PAGO_PILL[pill.tono] ?? PAGO_PILL.pendiente;
    return `<span class="${clase}" data-pago-pill>${escapeHTML(pill.texto)}</span>`;
}

/**
 * U4 — under a "Sin horas" pill: the active advances of the period that no
 * settlement will deduct (`js/admin.js` reads them in one batch). When that
 * read failed (`sinVerificar`), a neutral note instead, so the row never
 * looks like "no advances".
 * @param {number|null|undefined} totalPesos active advances total, in pesos (not centavos)
 * @param {boolean} [sinVerificar] the advances read failed
 * @returns {string}
 */
function renderAdelantosSinDescontar(totalPesos, sinVerificar = false) {
    if (sinVerificar) {
        return '<p role="note" class="mt-1 text-[11px] text-text-muted" data-adelantos-sin-verificar>No se pudieron verificar los adelantos de este período.</p>';
    }
    if (!(Number(totalPesos) > 0)) return '';
    return `<p class="mt-1 text-[11px] font-bold text-amber-800" data-adelantos-sin-descontar>Tiene adelantos por ${formatMonto(totalPesos)} en este período que no se van a descontar si no se liquida.</p>`;
}

/**
 * Spec liquidacion-otros-descuentos (B5): the same warning as
 * {@link renderAdelantosSinDescontar}, for the active otros descuentos of a
 * "Sin horas" row; a neutral note when that read failed.
 * @param {number|null|undefined} totalPesos
 * @param {boolean} [sinVerificar]
 * @returns {string}
 */
function renderOtrosDescuentosSinDescontar(totalPesos, sinVerificar = false) {
    if (sinVerificar) {
        return '<p role="note" class="mt-1 text-[11px] text-text-muted" data-otros-descuentos-sin-verificar>No se pudieron verificar los otros descuentos de este período.</p>';
    }
    if (!(Number(totalPesos) > 0)) return '';
    return `<p class="mt-1 text-[11px] font-bold text-amber-800" data-otros-descuentos-sin-descontar>Tiene otros descuentos por ${formatMonto(totalPesos)} en este período que no se van a descontar si no se liquida.</p>`;
}

/**
 * @param {{ id: string, nombre: string, activo: boolean, bucket: 'listo'|'anomalias'|'liquidada'|'sin_horas', anomaliasCount: number, liquidacionPeriodo: {desde:string,hasta:string}|null, trabajado: string, figuras: {bruto:number,consumos:number,adelantos:number,otros_descuentos:number,neto:number}|null, pill?: {texto:string,tono:string}|null, avisoPagoPrevio?: {total:number,periodos:Array<object>}|null, adelantosSinDescontar?: number|null, adelantosSinVerificar?: boolean, otrosDescuentosSinDescontar?: number|null, otrosDescuentosSinVerificar?: boolean, jornadasAbiertas?: Array<{fichadaId:string,entrada_at:string}> }} fila
 *   `adelantosSinDescontar` (U4, "Sin horas" rows only): active advances
 *   total of the period in PESOS (not centavos), `null` when there are none;
 *   `adelantosSinVerificar`: that read failed, so a neutral note is shown.
 *   `otrosDescuentosSinDescontar` / `otrosDescuentosSinVerificar`: the same
 *   two fields for the otros descuentos (spec liquidacion-otros-descuentos, B5).
 *   `jornadasAbiertas` (F-F): open shifts overlapping the period — a note under the pill.
 */
function renderStatusPill(fila) {
    let pillHtml;
    if (fila.bucket === 'anomalias') {
        // Copia literal del spec ("N anomalías") — sin singular/plural
        // especial, la cita exacta ya usa la forma plural para cualquier N.
        // S9: `<button>` (no `<span>`) — este pill ABRE el cajón de
        // anomalías; un `<span>` sin `tabindex` nunca es alcanzable por
        // teclado y nunca puede recibir el foco de vuelta al cerrar.
        pillHtml = `<button type="button" class="${PILL.anomalias}" data-action="tablero-abrir-anomalias" data-id="${escapeHTML(fila.id)}">${fila.anomaliasCount} anomalías</button>`;
    } else if (fila.bucket === 'liquidada') {
        const periodo = `${formatDiaMes(fila.liquidacionPeriodo?.desde)} a ${formatDiaMes(fila.liquidacionPeriodo?.hasta)}`;
        // S5b — estado de pago derivado (spec "Derived Payment State"), pill
        // secundaria al lado de "Liquidada · dd/mm a dd/mm", nunca reemplazándola.
        pillHtml = `<span class="${PILL.liquidada}">Liquidada · ${periodo}</span>${renderPagoPill(fila.pill)}`;
    } else if (fila.bucket === 'sin_horas') {
        // S2b — spec "Row Status Presentation": por hora/día con cero minutos
        // trabajados en el período; un `fijo` nunca llega acá (la policy pura
        // nunca lo clasifica "sin_horas", ver `liquidacion-tablero-policy.js`).
        pillHtml = `<span class="${PILL.sin_horas}">Sin horas</span>${renderAdelantosSinDescontar(fila.adelantosSinDescontar, fila.adelantosSinVerificar === true)}${renderOtrosDescuentosSinDescontar(fila.otrosDescuentosSinDescontar, fila.otrosDescuentosSinVerificar === true)}`;
    } else {
        pillHtml = `<span class="${PILL.listo}">Listo</span>`;
    }
    // S5b (AD18): el aviso de pagos previos aplica a CUALQUIER balde — un
    // empleado "Listo" a punto de volver a liquidarse también debe verlo.
    return `${pillHtml}${renderJornadaAbiertaNota(fila.jornadasAbiertas)}${renderFijoYaLiquidadoNota(fila)}${renderAvisoPagoPrevio(fila.avisoPagoPrevio)}`;
}

/**
 * F-F (owner decision 2026-09-27): la fila tiene un turno sin salida que toca
 * el período — el servidor rechazaría el confirm (409 `jornadas_abiertas`),
 * así que se avisa ANTES. Las cifras del preview se siguen mostrando.
 * @param {Array<{ entrada_at: string }>|null|undefined} jornadas `fila.jornadasAbiertas`
 * @returns {string}
 */
function renderJornadaAbiertaNota(jornadas) {
    const nota = notaTurnoSinSalida(jornadas);
    if (!nota) return '';
    return `<p role="note" class="mt-1 text-[11px] font-bold text-amber-800" data-jornada-abierta-nota>${escapeHTML(nota)}</p>`;
}

/**
 * L10/D1 (owner decisions 2026-09-27): un sueldo fijo que ya tiene una
 * liquidación confirmada de sueldo fijo que arranca en el mismo mes que el
 * período (`fijoYaLiquidadoEnMes`) — el servidor rechazaría el
 * confirm (409 `fijo_ya_liquidado_mes`), así que se avisa ANTES, con la misma
 * copia. Las cifras del preview se siguen mostrando.
 * @param {object} fila
 * @returns {string}
 */
function renderFijoYaLiquidadoNota(fila) {
    if (!fila.fijoYaLiquidado) return '';
    const nota = mensajeFijoYaLiquidadoMes(fila.nombre ?? '', fila.fijoYaLiquidado.mes);
    return `<p role="note" class="mt-1 text-[11px] font-bold text-amber-800" data-fijo-ya-liquidado-nota>${escapeHTML(nota)}</p>`;
}

/** @param {object} fila @returns {string} celda "Trabajado" (spec "Worked Time Column") — texto ya formateado por la policy pura, sólo se escapa acá. */
function renderTrabajadoCell(fila) {
    return `<td class="px-3 py-3 text-sm text-text-primary whitespace-nowrap">${escapeHTML(fila.trabajado ?? '—')}</td>`;
}

/** @param {object} fila @returns {string} las 5 columnas de plata — "—" para "N anomalías"/"Sin horas" (sin figuras). */
function renderMoneyCells(fila) {
    const f = fila.figuras;
    return `
        <td class="px-3 py-3 text-sm text-right text-text-primary">${f ? formatMonto(f.bruto) : '—'}</td>
        <td class="px-3 py-3 text-sm text-right text-text-primary">${f ? formatMonto(f.consumos) : '—'}</td>
        <td class="px-3 py-3 text-sm text-right text-text-primary">${f ? formatMonto(f.adelantos) : '—'}</td>
        <td class="px-3 py-3 text-sm text-right text-text-primary">${f ? formatMonto(f.otros_descuentos) : '—'}</td>
        <td class="px-3 py-3 text-sm text-right font-bold text-text-primary">${f ? formatMonto(f.neto) : '—'}</td>
    `;
}

/**
 * "Adelanto" (S4, spec "Row and Bulk Actions") — un adelanto se registra
 * contra una FECHA explícita que el propio cajón pide, no contra el período
 * seleccionado en el tablero, así que se ofrece en TODA fila sin importar
 * el bucket (a diferencia de "Liquidar", que solo aplica a "Listo"/"Sin
 * horas"): un empleado "N anomalías" o ya "Liquidada" para este período
 * igual puede necesitar cargar un adelanto fechado en un período distinto.
 * El servidor (`crear_adelanto`) es quien rechaza `periodo_ya_liquidado` si
 * la fecha elegida cae dentro de un período ya congelado — esta vista no
 * intenta adivinarlo de antemano.
 * @param {object} fila
 * @returns {string}
 */
function renderAdelantoBtn(fila) {
    return `<button type="button" data-action="tablero-adelanto" data-id="${escapeHTML(fila.id)}" class="${ADELANTO_BTN}">Adelanto</button>`;
}

/**
 * "Descontar" (spec liquidacion-otros-descuentos, B1): abre el cajón "Otros
 * descuentos". Mismo criterio que "Adelanto": va en TODA fila, porque el
 * descuento se carga contra una fecha explícita y el servidor
 * (`crear_otro_descuento`) es quien rechaza `periodo_ya_liquidado`.
 * @param {object} fila
 * @returns {string}
 */
function renderDescontarBtn(fila) {
    return `<button type="button" data-action="tablero-descontar" data-id="${escapeHTML(fila.id)}" class="${ADELANTO_BTN}">Descontar</button>`;
}

/** Las acciones de movimiento de sueldo que van en toda fila: "Adelanto" y "Descontar". @param {object} fila @returns {string} */
function renderMovimientosBtns(fila) {
    return `${renderAdelantoBtn(fila)}${renderDescontarBtn(fila)}`;
}

/**
 * "Registrar pago" (S5b, spec "Row and Bulk Actions": acción válida sólo en
 * filas "Liquidada" — es la única con un `liquidacion_id`/neto congelado
 * contra el que anclar un pago).
 * @param {object} fila
 * @returns {string}
 */
function renderPagoBtn(fila) {
    return `<button type="button" data-action="tablero-pago" data-id="${escapeHTML(fila.id)}" class="${ADELANTO_BTN}">Registrar pago</button>`;
}

/** @param {object} fila @param {boolean} bloqueado review-62957fd6e8912f8e: refrescando o liquidando en curso — mismo criterio que la barra en lote. */
function renderAcciones(fila, bloqueado) {
    if (fila.bucket === 'listo') {
        // F-F: con un turno sin salida en el período el confirm se rechazaría
        // seguro — el botón queda visible pero deshabilitado, con la nota de
        // la columna de estado diciendo por qué (mismo criterio que "Sin horas").
        // L10/D1: lo mismo para un fijo con su sueldo del mes ya liquidado.
        const deshabilitado = bloqueado || fila.jornadasAbiertas?.length > 0 || Boolean(fila.fijoYaLiquidado);
        return `<button type="button" data-action="tablero-liquidar" data-id="${escapeHTML(fila.id)}" class="${LIQUIDAR_BTN}" ${deshabilitado ? 'disabled aria-disabled="true"' : ''}>Liquidar</button>${renderMovimientosBtns(fila)}`;
    }
    // S2b — spec "Row Status Presentation": "'Liquidar' is disabled" on a
    // "Sin horas" row — rendered, not omitted, so the admin can SEE the
    // action exists but cannot use it (same button, `disabled` attribute).
    if (fila.bucket === 'sin_horas') {
        return `<button type="button" data-action="tablero-liquidar" data-id="${escapeHTML(fila.id)}" class="${LIQUIDAR_BTN}" disabled aria-disabled="true">Liquidar</button>${renderMovimientosBtns(fila)}`;
    }
    if (fila.bucket === 'liquidada') {
        return `${renderPagoBtn(fila)}${renderMovimientosBtns(fila)}`;
    }
    return renderMovimientosBtns(fila);
}

/** @param {object} fila @param {boolean} bloqueado */
function renderFilaRow(fila, bloqueado) {
    return `
        <tr data-tablero-fila data-id="${escapeHTML(fila.id)}" data-bucket="${escapeHTML(fila.bucket)}">
            <td class="px-3 py-3 text-sm text-text-primary">${escapeHTML(fila.nombre)}${fila.activo ? '' : ' <span class="text-[10px] text-text-muted uppercase tracking-widest">(inactivo)</span>'}</td>
            <td class="px-3 py-3">${renderStatusPill(fila)}</td>
            ${renderTrabajadoCell(fila)}
            ${renderMoneyCells(fila)}
            <td class="px-3 py-3 text-right">${renderAcciones(fila, bloqueado)}</td>
        </tr>
    `;
}

/**
 * S8 — "Exportar CSV" del historial mensual, REUBICADO acá desde el toolbar
 * legacy de `LiquidacionView.js` (que se retira junto con
 * `renderRangoForm`/`renderPreviewSection`, ver apply-progress.md "Unit
 * S8"): spec `payroll-period-dashboard` "Row and Bulk Actions" lista
 * "Exportar CSV" como la TERCERA acción de la barra en lote, junto a
 * "Liquidar N listos"/"Imprimir comprobantes (N)". El comportamiento y la
 * política de exportación (`js/shared/liquidacion-export-policy.js`) NO
 * cambian — sólo se mudó dónde vive el form; `js/admin.js` sigue leyendo
 * `mes-export`/`incluir-anuladas-export` recién al clickear (AD16: ningún
 * cambio repinta el mount).
 * @param {{ mes?: string, incluirAnuladas?: boolean, exportando?: boolean, mensaje?: string|null }} [exportState]
 * @returns {string}
 */
function renderExportForm(exportState = {}) {
    const mes = exportState.mes ?? '';
    const incluirAnuladas = Boolean(exportState.incluirAnuladas);
    const exportando = Boolean(exportState.exportando);
    const avisoHtml = exportState.mensaje
        ? `<p role="status" class="text-xs text-text-muted mt-2 basis-full" data-export-aviso>${escapeHTML(exportState.mensaje)}</p>`
        : '';
    return `
        <form data-action-form="tablero-export" class="flex flex-wrap items-center gap-2">
            <label class="sr-only" for="tablero-export-mes">Mes a exportar</label>
            <input id="tablero-export-mes" type="month" name="mes-export" value="${escapeHTML(mes)}" class="${EXPORT_FIELD}" />
            <label class="flex items-center gap-1 text-[10px] text-text-muted whitespace-nowrap">
                <input type="checkbox" name="incluir-anuladas-export" ${incluirAnuladas ? 'checked' : ''} class="w-3.5 h-3.5 rounded border-border-subtle text-brand focus:ring-brand" />
                Incluir anuladas
            </label>
            <button type="button" data-action="tablero-exportar" ${exportando ? 'disabled' : ''} class="${BULK_EXPORTAR_BTN}">Exportar CSV</button>
        </form>
        ${avisoHtml}
    `;
}

/**
 * Barra de acciones en lote (S7/S8, spec "Row and Bulk Actions"/"Bulk Print
 * Job"): "Liquidar N listos" cuenta las filas "Listo" del período vigente
 * (nunca "Sin horas"/"N anomalías"/"Liquidada" — mismos ids que entran al
 * batch de `preview`); "Imprimir comprobantes (N)" cuenta las filas
 * "Liquidada" (las únicas con un documento congelado que imprimir, AD16);
 * "Exportar CSV" (S8) exporta el MES elegido en su propio mini-form, no el
 * período seleccionado en el toolbar de arriba — mismo criterio que el
 * toolbar legacy del que se mudó (AD11 original: "SIEMPRE refetchea por su
 * cuenta"). Ambos contadores se derivan de `filas` acá mismo — la vista es
 * pura, no necesita que `js/admin.js` le mande los conteos aparte.
 *
 * U3: "Liquidar N listos" cuenta `loteLiquidable(filas, rango).ids` — la
 * MISMA función que arma el batch en `js/admin.js` — así que en un período
 * que no es un mes calendario completo los sueldos fijos quedan fuera del
 * conteo, con un aviso que dice por qué. F-F: lo mismo para las filas con
 * un turno sin salida en el período (`lote.jornadasAbiertasExcluidas`).
 * @param {Array<object>} filas
 * @param {boolean} bloqueado refrescando O liquidando (review-62957fd6e8912f8e)
 * @param {{ mes?: string, incluirAnuladas?: boolean, exportando?: boolean, mensaje?: string|null }} [exportState]
 * @param {{ desde: string, hasta: string }|null} [rango] período vigente del tablero
 * @param {'hoy'|'futuro'|null} [periodoNoTerminado] L1/D2: el período termina hoy (ART) o después
 * @returns {string}
 */
function renderBulkBar(filas, bloqueado, exportState, rango = null, periodoNoTerminado = null) {
    const lote = loteLiquidable(filas, rango);
    const loteCount = lote.ids.length;
    const liquidadaCount = filas.filter((f) => f.bucket === 'liquidada').length;
    const avisoFijos = lote.fijosExcluidos.length
        ? '<p role="note" class="basis-full text-xs text-text-muted" data-lote-fijos-aviso>Los sueldos fijos se liquidan por mes: no se incluyen en el lote de este período.</p>'
        : '';
    const avisoFijosYaLiquidados = lote.fijosYaLiquidadosExcluidos.length
        ? '<p role="note" class="basis-full text-xs text-text-muted" data-lote-fijos-ya-liquidados-aviso>Quien tiene su sueldo fijo ya liquidado en el mes no entra en el lote.</p>'
        : '';
    const avisoJornadasAbiertas = lote.jornadasAbiertasExcluidas.length
        ? '<p role="note" class="basis-full text-xs text-text-muted" data-lote-jornadas-abiertas-aviso>Quien tiene un turno sin salida en el período no entra en el lote hasta cerrarlo.</p>'
        : '';
    const cuandoSeLiquida = periodoNoTerminado === 'hoy' ? 'se liquida desde mañana' : 'se liquida cuando termine';
    const avisoNoTerminado = periodoNoTerminado
        ? `<p role="note" class="basis-full text-xs text-text-muted" data-periodo-no-terminado-aviso>Este período todavía no terminó: podés ver las cifras, pero ${cuandoSeLiquida}.</p>`
        : '';
    return `
        <div class="flex flex-wrap items-center gap-3 mb-3" data-tablero-bulk-bar>
            <button type="button" data-action="tablero-liquidar-lote" class="${BULK_LIQUIDAR_BTN}" ${loteCount === 0 || bloqueado ? 'disabled' : ''}>Liquidar ${loteCount} listos</button>
            <button type="button" data-action="tablero-imprimir-lote" class="${BULK_IMPRIMIR_BTN}" ${liquidadaCount === 0 ? 'disabled' : ''}>Imprimir comprobantes (${liquidadaCount})</button>
            ${renderExportForm(exportState)}
            ${avisoNoTerminado}
            ${avisoFijos}
            ${avisoFijosYaLiquidados}
            ${avisoJornadasAbiertas}
        </div>
    `;
}

/**
 * @param {{bruto:number,consumos:number,adelantos:number,otros_descuentos:number,neto:number}|null} totales
 * @param {'ok'|'error'|null} estado AD19: un `preview` degradado apaga la fila de totales a "—" igual que cada celda de plata individual — nunca una suma de figuras que en realidad no llegaron.
 */
function renderTotalsRow(totales, estado) {
    if (!totales) return '';
    const celda = (valor) => (estado === 'error' ? '—' : formatMonto(valor));
    return `
        <tr class="border-t-2 border-border-subtle font-black">
            <td class="px-3 py-3 text-sm text-text-primary" colspan="3">Totales</td>
            <td class="px-3 py-3 text-sm text-right text-text-primary">${celda(totales.bruto)}</td>
            <td class="px-3 py-3 text-sm text-right text-text-primary">${celda(totales.consumos)}</td>
            <td class="px-3 py-3 text-sm text-right text-text-primary">${celda(totales.adelantos)}</td>
            <td class="px-3 py-3 text-sm text-right text-text-primary">${celda(totales.otros_descuentos)}</td>
            <td class="px-3 py-3 text-sm text-right text-text-primary">${celda(totales.neto)}</td>
            <td class="px-3 py-3"></td>
        </tr>
    `;
}

/**
 * @param {{
 *   seleccion: object|null,
 *   cargando: boolean,
 *   refrescando: boolean,
 *   liquidando: boolean,
 *   periodoNoTerminado?: 'hoy'|'futuro'|null,
 *   mostrarInactivos: boolean,
 *   desde: string|null,
 *   hasta: string|null,
 *   filas: Array<object>,
 *   totales: {bruto:number,consumos:number,adelantos:number,neto:number}|null,
 *   calculadoAt: string|null,
 *   estado: 'ok'|'error'|null,
 *   error: { kind: TableroErrorKind, mensaje: string }|null,
 *   draft: { desde?: string, hasta?: string }|null,
 *   stale: boolean,
 *   exportState: { mes?: string, incluirAnuladas?: boolean, exportando?: boolean, mensaje?: string|null }|null,
 * }} [model]
 * @returns {string}
 */
export function LiquidacionTableroView(model = {}) {
    if (model.cargando) {
        return `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="status" aria-live="polite">
                <i class="fa-solid fa-circle-notch fa-spin text-2xl text-brand mb-4" aria-hidden="true"></i>
                <p class="text-xs font-black text-text-muted uppercase tracking-widest">Cargando...</p>
            </div>
        `;
    }

    const seleccion = model.seleccion ?? null;
    const activo = presetActivo(seleccion);
    const presetButtons = PRESETS.map(
        (p) => `
            <button type="button" data-action="tablero-preset" data-kind="${p.kind}" data-active="${p.kind === activo}" class="${PRESET_BTN}" ${model.refrescando ? 'disabled' : ''}>${p.label}</button>
        `,
    ).join('');

    const puedeAnterior = seleccion?.kind !== 'personalizado';
    const puedeSiguiente = seleccion?.kind !== 'personalizado';

    // review-982449c9e9c27e08 (findings 1/2/3, AD19): banner + "Reintentar" —
    // nunca una tabla vacía silenciosa. S2b follow-up b: SIN fallback propio
    // acá — quien arma `tableroCache.error` (`js/admin.js`) siempre manda un
    // `mensaje` no vacío (incluido el fallback para el único envelope real
    // cuyo `mensaje` viene vacío, `kind: 'anomalias'`); esta vista confía en
    // eso en vez de duplicar esa copia acá.
    const errorBanner =
        model.estado === 'error'
            ? `
        <div class="flex items-center justify-between gap-3 px-4 py-3 mb-3 rounded-xl bg-red-50 border border-red-200 text-red-800" role="alert">
            <span class="text-xs font-bold">${escapeHTML(model.error?.mensaje ?? '')}</span>
            <button type="button" data-action="tablero-reintentar" class="${ACTUALIZAR_BTN}" ${model.refrescando ? 'disabled' : ''}>Reintentar</button>
        </div>
    `
            : '';

    const filas = model.filas ?? [];
    // review-62957fd6e8912f8e: un recompute (`refrescando`) o un
    // preview/confirm en vuelo (`liquidando`) bloquean "Liquidar" por igual.
    // L1: un período que todavía no terminó tampoco se liquida (ni por fila ni
    // en lote) — las cifras se siguen mostrando.
    const bloqueado = Boolean(model.refrescando) || Boolean(model.liquidando) || Boolean(model.periodoNoTerminado);
    // S8: la barra en lote SIEMPRE se renderiza (antes, `filas.length ?` la
    // ocultaba entera con un roster vacío) — "Exportar CSV" exporta un MES
    // elegido en su propio mini-form, independiente del período/roster
    // vigente en el tablero (mismo criterio AD11 original: "SIEMPRE
    // refetchea por su cuenta"), así que nunca puede depender de si HAY
    // filas para mostrar. "Liquidar 0 listos"/"Imprimir comprobantes (0)"
    // simplemente quedan deshabilitados (ya lo hacían) en vez de ausentes.
    const rango = model.desde && model.hasta ? { desde: model.desde, hasta: model.hasta } : null;
    const bulkBar = renderBulkBar(filas, bloqueado, model.exportState, rango, model.periodoNoTerminado ?? null);
    // S2b — spec "Degraded and Empty States": un roster vacío (nunca un
    // fallo, AD19 ya tiene su propio banner arriba) muestra la copia + el
    // hint del toggle, en vez de una tabla vacía silenciosa.
    const rosterVacio = filas.length === 0 && model.estado !== 'error';
    const tablaHtml = filas.length
        ? `
            <table class="w-full text-left">
                <thead>
                    <tr class="text-[10px] font-black uppercase tracking-widest text-text-muted border-b border-border-subtle">
                        <th class="px-3 py-2">Empleado</th>
                        <th class="px-3 py-2">Estado</th>
                        <th class="px-3 py-2">Trabajado</th>
                        <th class="px-3 py-2 text-right">Bruto</th>
                        <th class="px-3 py-2 text-right">Consumos</th>
                        <th class="px-3 py-2 text-right">Adelantos</th>
                        <th class="px-3 py-2 text-right whitespace-nowrap">Otros desc.</th>
                        <th class="px-3 py-2 text-right">Neto</th>
                        <th class="px-3 py-2 text-right">Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    ${filas.map((f) => renderFilaRow(f, bloqueado)).join('')}
                    ${renderTotalsRow(model.totales, model.estado)}
                </tbody>
            </table>
        `
        : rosterVacio
            ? `
            <div class="text-center py-12">
                <p class="text-sm font-bold text-text-primary mb-2">No hay empleados activos en este período.</p>
                <p class="text-xs text-text-muted">Probá «Mostrar inactivos con horas en el período».</p>
            </div>
        `
            : '';

    return `
        <section aria-label="Tablero de liquidación" class="mb-8">
            <div class="flex items-center justify-between mb-4">
                <h2 class="text-lg font-black uppercase tracking-tight text-text-primary">Liquidación del período</h2>
                <div class="flex items-center gap-3">
                    <span class="text-xs font-bold text-text-muted">${formatFechaLarga(model.desde)} a ${formatFechaLarga(model.hasta)}</span>
                    <button type="button" data-action="tablero-config-abrir" aria-label="Configuración" class="${CONFIG_BTN}">
                        <i class="fa-solid fa-gear" aria-hidden="true"></i> Configuración
                    </button>
                </div>
            </div>

            <div class="flex flex-wrap items-center gap-2 mb-2">
                ${presetButtons}
                <button type="button" data-action="tablero-anterior" class="${NAV_BTN}" aria-label="Período anterior" ${puedeAnterior ? '' : 'disabled'}>‹ anterior</button>
                <button type="button" data-action="tablero-siguiente" class="${NAV_BTN}" aria-label="Período siguiente" ${puedeSiguiente ? '' : 'disabled'}>siguiente ›</button>
            </div>

            ${renderPersonalizadoForm(model.draft, seleccion, activo === 'personalizado')}

            <div class="flex items-center justify-between flex-wrap gap-3 my-4">
                <div class="flex items-center gap-3">
                    <span class="text-[11px] font-bold text-text-muted">
                        Cifras al ${formatHoraSolo(model.calculadoAt)}${model.stale ? ' · <span class="text-amber-700">desactualizadas</span>' : ''}
                    </span>
                    <button type="button" data-action="tablero-actualizar" class="${ACTUALIZAR_BTN}" ${model.refrescando ? 'disabled' : ''}>Actualizar</button>
                </div>
                <label class="flex items-center gap-2 text-xs text-text-primary">
                    <input type="checkbox" data-action="tablero-mostrar-inactivos" ${model.mostrarInactivos ? 'checked' : ''} class="w-4 h-4 rounded border-border-subtle text-brand focus:ring-brand" />
                    Mostrar inactivos con horas en el período
                </label>
            </div>

            ${errorBanner}
            ${bulkBar}

            <div class="bg-surface border border-border-subtle rounded-2xl overflow-x-auto">
                ${tablaHtml}
            </div>
        </section>
    `;
}
